import React, { FC, PropsWithChildren } from "react";
import {
  FlatList,
  FlatListProps,
  Platform,
  StyleProp,
  StyleSheet,
  TextProps,
  TouchableOpacity,
  TouchableOpacityProps,
  View,
  ViewProps,
  ViewStyle,
} from "react-native";

import { PdIconButton } from "src/components/forms/icon-button";
import { PdArrowLeft, PdArrowRight, PdChevronDown, PdChevronUp } from "src/components/Icons";
import { PdStack, PdText, tPdText } from "src/components/layout";
import { tHeaderInstance } from "src/hooks/table";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { PdButton, PdButtonLabel, tPdButtonProps } from "../forms/button";

export type tPdTable<T> = {
  isMobile?: boolean;
} & FlatListProps<T>;

export const PdTab: React.FC<{ isActive?: boolean } & tPdButtonProps> = ({
  isActive,
  contentContainerStyle,
  ...rest
}) => (
  <PdButton
    size="sm"
    colorScheme="gray"
    contentContainerStyle={[isActive ? styles.pdTabActive : {}, contentContainerStyle]}
    {...rest}
  />
);

export const PdTabLabel: React.FC<TextProps> = ({ style, ...rest }) => <PdButtonLabel style={style} {...rest} />;

export const PdTableHeader: React.FC<tPdText> = ({ style, ...rest }) => (
  <PdText style={[styles.pdTableHeaderLabel, style]} {...rest} />
);

export const PdTableRow: FC<ViewProps & { isHovered?: boolean; isPressed?: boolean }> = ({
  isHovered,
  isPressed,
  style,
  ...rest
}) => (
  <View
    style={[
      styles.pdTableRow,
      isHovered ? styles.pdTableRowHovered : {},
      isPressed ? styles.pdTableRowPressed : {},
      style,
    ]}
    {...rest}
  />
);

export const PdTableCell: FC<ViewProps> = ({ style, ...rest }) => (
  <View style={[styles.pdTableCell, style]} {...rest} />
);

export const PdTableHeaderCell: React.FC<TouchableOpacityProps> = ({ style, ...rest }) => (
  <TouchableOpacity style={[styles.pdTableHeaderCell, style]} {...rest} />
);

export const PdTableCellText: FC<tPdText> = ({ style, ...rest }) => (
  <PdText numberOfLines={1} style={style} {...rest} />
);

export const PdTableRowMobileLabel: React.FC<tPdText> = ({ style, ...rest }) => (
  <PdText variant="body-sm" style={[styles.pdTableRowMobileLabel, style]} {...rest} />
);

export const PdTableRowMobileValue: React.FC<tPdText> = ({ style, ...rest }) => (
  <PdText variant="body-sm" style={[styles.pdTableRowMobileValue, style]} {...rest} />
);

export const PdTableHeaderRow: React.FC<ViewProps> = ({ style, ...rest }) => (
  <View style={[style, styles.pdTableHeaderRow]} {...rest} />
);

interface iPdTableRowMobileContext {
  isOpen: boolean;
  toggle: () => void;
}

const PdTableRowMobileContext = React.createContext<Partial<iPdTableRowMobileContext>>({});

export const PdTableRowMobile: React.FC<ViewProps> = ({ style, ...rest }) => {
  const [isOpen, setOpen] = React.useState(false);

  const toggle = () => {
    setOpen((prev) => !prev);
  };

  return (
    <PdTableRowMobileContext.Provider value={{ isOpen, toggle }}>
      <View style={[styles.pdTableRowMobile, style]} {...rest} />
    </PdTableRowMobileContext.Provider>
  );
};

export const PdTableRowMobileButton: React.FC<TouchableOpacityProps> = ({ style, children, ...rest }) => {
  const { isOpen, toggle } = React.useContext(PdTableRowMobileContext);

  return (
    <TouchableOpacity onPress={toggle} style={[styles.pdTableRowMobileButton, style]} {...rest}>
      <View style={styles.pdTableRowMobileButtonChevronIconContainer}>
        {isOpen ? (
          <PdChevronUp color={kColors.white} style={styles.pdTableRowMobileButtonChevronUpIcon} />
        ) : (
          <PdChevronDown color={kColors.white} style={styles.pdTableRowMobileButtonChevronDownIcon} />
        )}
      </View>
      {children}
    </TouchableOpacity>
  );
};

export const PdTableRowMobilePanel: React.FC = ({ children }) => {
  const { isOpen } = React.useContext(PdTableRowMobileContext);

  if (!isOpen) return null;

  return <>{children}</>;
};

export const PdTableEmpty: React.FC<{ message?: string }> = ({ message = "There is nothing to display" }) => {
  return (
    <View style={styles.pdTableEmpty}>
      <PdText style={styles.pdTableEmptyLabel}>{message}</PdText>
    </View>
  );
};

export const PdTableMobileItemSeparator: React.FC<ViewProps> = ({ style, ...rest }) => (
  <View style={[{ height: kStyleConst.kSpacing * 2 }, style]} {...rest} />
);

export const PdTable = <T extends unknown>(props: PropsWithChildren<tPdTable<T>>): React.ReactElement | null => {
  const { isMobile, style, contentContainerStyle, ...rest } = props;

  return (
    <FlatList
      style={[
        isMobile ? {} : styles.pdTable,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        Platform.OS === "web" ? (webStyles.overflowAuto as StyleProp<ViewStyle>) : {},
        style,
      ]}
      contentContainerStyle={[Platform.OS === "web" && !isMobile ? styles.mrAuto : {}, contentContainerStyle]}
      {...rest}
    />
  );
};

export const PdTableSortableHeaders = <T extends unknown>({
  headers,
}: {
  headers: tHeaderInstance<T>[];
}): JSX.Element => {
  return (
    <PdTableHeaderRow>
      {headers.map((header) => (
        <PdTableHeaderCell
          key={header.id}
          style={header.style}
          disabled={header.isSortable === false}
          onPress={header.sort}
        >
          {typeof header.header === "string" ? (
            <PdTableHeader>{header.header}</PdTableHeader>
          ) : (
            header.header({ style: header.style })
          )}
          {!(header.isSortable === false) && (
            <View style={styles.pdTableHeaderIcon}>
              {header.isSorted && header.sortDirection === "asc" ? (
                <PdChevronUp style={styles.pdTableHeaderChevron} />
              ) : (
                <PdChevronDown style={styles.pdTableHeaderChevron} />
              )}
            </View>
          )}
        </PdTableHeaderCell>
      ))}
    </PdTableHeaderRow>
  );
};

export const PdTablePaginationFooter: React.FC<{
  page: number;
  totalPages?: number;
  hasNext?: boolean;
  onPressBack: () => void;
  onPressNext: () => void;

  style?: StyleProp<ViewStyle>;
}> = ({ page, hasNext, onPressBack, onPressNext, style }) => {
  return (
    <PdStack direction="horizontal" style={[styles.alignCenter, styles.mlAuto, style]}>
      <PdText>{`Page ${page}`}</PdText>
      <PdIconButton
        onPress={onPressBack}
        isDisabled={page === 1}
        variant="ghost"
        icon={<PdArrowLeft style={styles.pdTablePaginationFooterIcon} />}
      />
      <PdIconButton
        onPress={onPressNext}
        isDisabled={!hasNext}
        variant="ghost"
        icon={<PdArrowRight style={styles.pdTablePaginationFooterIcon} />}
      />
    </PdStack>
  );
};

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  mlAuto: {
    marginLeft: "auto",
  },
  mrAuto: {
    marginRight: "auto",
  },
  pdTabActive: {
    backgroundColor: kColors.green100,
    borderColor: kColors.green100,
  },
  pdTable: {
    borderColor: kColors.gray300,
    borderRadius: 5,
    borderWidth: 1,
  },
  pdTableCell: {
    alignItems: "center",
    flexDirection: "row",
    height: 40,
    marginRight: kStyleConst.kSpacing * 2,
  },
  pdTableEmpty: {
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
    paddingVertical: kStyleConst.kSpacing * 2,
  },
  pdTableEmptyLabel: {
    fontStyle: "italic",
  },
  pdTableHeaderCell: {
    alignItems: "flex-end",
    flexDirection: "row",
    // flex: 1,
    marginRight: kStyleConst.kSpacing * 2,
  },
  pdTableHeaderChevron: {
    color: kColors.brand500,
  },
  pdTableHeaderIcon: {
    alignItems: "center",
    height: 8,
    justifyContent: "center",
    marginBottom: 2,
    width: 8,
  },
  pdTableHeaderLabel: {
    color: kColors.brand500,
    fontFamily: "Raleway",
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 0.8,
    marginRight: kStyleConst.kSpacing,
    textTransform: "uppercase",
  },
  pdTableHeaderRow: {
    backgroundColor: kColors.lightGray,
    flexDirection: "row",
    minWidth: "100%",
    paddingHorizontal: kStyleConst.kSpacing * 5,
    paddingVertical: kStyleConst.kSpacing * 3,
  },
  pdTablePaginationFooterIcon: {
    width: 20,
  },
  pdTableRow: {
    alignItems: "center",
    flexDirection: "row",
    height: 40,
    justifyContent: "center",
    paddingHorizontal: kStyleConst.kSpacing * 5,
  },
  pdTableRowHovered: {
    backgroundColor: kColors.gray50,
  },
  pdTableRowMobile: {
    backgroundColor: kColors.lightGray,
    borderRadius: 6,
  },
  pdTableRowMobileButton: {
    alignItems: "center",
    flexDirection: "row",
    padding: kStyleConst.kSpacing * 2,
  },
  pdTableRowMobileButtonChevronDownIcon: {
    height: 10,
    marginTop: 1,
    width: 10,
  },
  pdTableRowMobileButtonChevronIconContainer: {
    alignItems: "center",
    backgroundColor: kColors.brand500,
    borderRadius: 999,
    height: 25,
    justifyContent: "center",
    marginRight: kStyleConst.kSpacing * 2,
    width: 25,
  },
  pdTableRowMobileButtonChevronUpIcon: {
    height: 10,
    width: 10,
  },
  pdTableRowMobileLabel: {
    flex: 1,
    fontWeight: "500",
    textTransform: "uppercase",
  },
  pdTableRowMobileValue: {
    flex: 2,
  },
  pdTableRowPressed: {
    opacity: 0.6,
  },
});

const webStyles: { [key: string]: React.CSSProperties } = {
  overflowAuto: {
    overflow: "auto",
  },
} as const;
